'use client'

import Image from 'next/image'
import { poweredByRedyUrl } from '@/lib/marketing/poweredBy'

/**
 * "Powered by Redy" badge for customer-facing surfaces. Es un link a
 * useredy.com (pestaña nueva): el cliente que ve la tienda de un local es
 * justo quien puede querer Redy para su propio negocio.
 *
 * Visibility is controlled by the `hide_powered_by_branding` feature flag on the
 * location. Free-tier merchants show the badge; paid-tier merchants can have it
 * hidden by Redy ops (the flag is not in the merchant admin UI).
 *
 * Designed to sit on top of any background — uses a glass material that reads
 * over both dark and light surfaces. Recibe toques (antes tenía
 * `pointer-events: none`): ocupa solo su pastilla en la esquina, y en las
 * páginas que lo montan (micrositio y mesa) no hay nada fijo debajo de ella.
 */
export function RedyBrandBadge({
  slug,
  placement = 'badge',
  hidden = false,
  position = 'bottom-right',
  tone = 'dark',
}: {
  /** Slug del local: va en `utm_source` para saber qué tienda manda la visita. */
  slug: string
  /** `table_badge` en la página de la mesa; `badge` en la tienda. */
  placement?: 'badge' | 'table_badge'
  hidden?: boolean
  /** Corner placement. Bottom-right is the convention; override for layouts that need it elsewhere. */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /** Surface tone behind the badge — picks the right logo + readable text/border colors. */
  tone?: 'dark' | 'light'
}) {
  if (hidden) return null

  const isDark = tone === 'dark'
  const logoSrc = isDark ? '/redy-logo-white.png' : '/redy-logo.png'

  const positionStyles: Record<typeof position, React.CSSProperties> = {
    // --ms-order-bar-h: alto de la barra «Ver mi pedido» de la tienda online
    // (OrderBar). Sin barra, o fuera de la tienda, vale 0 y queda donde siempre.
    'bottom-right': { bottom: 'calc(16px + var(--ms-order-bar-h, 0px))', right: 16 },
    'bottom-left': { bottom: 'calc(16px + var(--ms-order-bar-h, 0px))', left: 16 },
    'top-right': { top: 16, right: 16 },
    'top-left': { top: 16, left: 16 },
  }

  return (
    <a
      href={poweredByRedyUrl({ slug, campaign: 'storefront', placement })}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Powered by Redy — useredy.com"
      style={{
        position: 'fixed',
        ...positionStyles[position],
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 999,
        background: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(26,26,26,0.06)',
        backdropFilter: 'blur(12px) saturate(140%)',
        WebkitBackdropFilter: 'blur(12px) saturate(140%)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(26,26,26,0.08)'}`,
        color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(26,26,26,0.55)',
        fontFamily: "'Satoshi', system-ui, sans-serif",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        cursor: 'pointer',
        // Debajo de los popups/sheets (z-50) para no taparlos, pero por encima
        // del contenido normal de la página.
        zIndex: 40,
        userSelect: 'none',
      }}
    >
      <span>Powered by</span>
      <Image
        src={logoSrc}
        alt=""
        width={42}
        height={14}
        style={{ height: 12, width: 'auto', objectFit: 'contain' }}
        priority
      />
    </a>
  )
}
